import type { MasterDie, MaterialId } from './types';
import { BALANCE } from '../data/balance';
import { MATERIALS } from '../data/materials';
import type { NodeLevels } from './skilltree';

/**
 * Bonus des **Matériaux** — v0.47.0, révision du GD du 15/10
 * (`docs/INFINI_PROPOSITION.md` §5), arbitrages **A94** (réponses 14 et 16) et
 * **A95** (réponses 1 à 3).
 *
 * Les « pièces » d'un Matériau sont les dés Maîtres **équipés** qui le portent
 * (A95-1) ; un dé sans Matériau ne compte jamais. Le bonus à 3 pièces s'active
 * avec *Matériau* (`aby_material`), celui à 5 pièces avec *Matériologie*
 * (`aby_materiology`). Les bonus se lisent **à chaque lancer** : changer de
 * dés en Accalmie suffit à les allumer ou les éteindre.
 *
 * Où chaque bonus agit :
 *  - *Boisé 3* : `Game.tick` (Invasion, sous 5 s, +10 s une fois) ;
 *  - *Boisé 5*, *Ferreux 3 / 5* : `buildThrow`, dans `D_L` (dés Basiques) ;
 *  - *Osseux 3 / 5* : `Game.applyThrow` (bris) et `buildThrow` (`brokenMult`) ;
 *  - *Caoutchouc 3 / 5* : `buildThrow` (rebonds des emplacements 1, puis 1 à 3) ;
 *  - *Silicium 3* : `Game.applyThrow` (excédent) et `buildThrow` (report) ;
 *  - *Silicium 5* : `buildThrow` (dé fantôme) ;
 *  - *Du Vide 3* : apparition de l'ennemi (`voidEnemyEffect`) ;
 *  - *Du Vide 5* : `buildThrow` (`max(total, précédent)`).
 *
 * Fonctions **pures**.
 */

/** Un bonus de Matériau actif : le Matériau et son palier (3 ou 5 pièces). */
export interface ActiveMaterialBonus {
  material: MaterialId;
  tier: 3 | 5;
}

/** Nombre de dés Maîtres équipés par Matériau (tous les Matériaux présents, 0 par défaut). */
export function materialSets(equipped: readonly MasterDie[]): Record<MaterialId, number> {
  const out = Object.fromEntries(MATERIALS.map((m) => [m.id, 0])) as Record<MaterialId, number>;
  for (const die of equipped) {
    const id = die.material;
    if (id && Object.prototype.hasOwnProperty.call(out, id)) out[id] += 1;
  }
  return out;
}

/**
 * Bonus actifs, dans l'ordre du catalogue puis du palier : 3 pièces si
 * `aby_material ≥ 1` et au moins 3 dés équipés du Matériau, 5 pièces si
 * `aby_materiology ≥ 1` et au moins 5. Les deux paliers sont indépendants
 * (chacun sa porte, A94-3) : en pratique *Matériologie* (porte 25) suit
 * toujours *Matériau* (porte 13).
 */
export function activeMaterialBonuses(
  equipped: readonly MasterDie[],
  levels: NodeLevels,
): ActiveMaterialBonus[] {
  const sets = materialSets(equipped);
  const small = (levels.aby_material ?? 0) >= 1;
  const large = (levels.aby_materiology ?? 0) >= 1;
  const out: ActiveMaterialBonus[] = [];
  for (const def of MATERIALS) {
    const n = sets[def.id];
    if (small && n >= BALANCE.materialSetSmall) out.push({ material: def.id, tier: 3 });
    if (large && n >= BALANCE.materialSetLarge) out.push({ material: def.id, tier: 5 });
  }
  return out;
}

/** Le bonus `material` au palier `tier` est-il dans la liste ? */
export function hasMaterialBonus(
  bonuses: readonly ActiveMaterialBonus[] | null | undefined,
  material: MaterialId,
  tier: 3 | 5,
): boolean {
  return (bonuses ?? []).some((b) => b.material === material && b.tier === tier);
}

/**
 * *Caoutchouc* (A94-16) : nombre d'emplacements (à partir du 1er) dont le dé
 * rebondit — 3 avec le bonus à 5 pièces, 1 avec celui à 3, 0 sinon.
 */
export function rubberSlots(bonuses: readonly ActiveMaterialBonus[] | null | undefined): number {
  if (hasMaterialBonus(bonuses, 'rubber', 5)) return BALANCE.rubberSlotsLarge;
  if (hasMaterialBonus(bonuses, 'rubber', 3)) return BALANCE.rubberSlotsSmall;
  return 0;
}

/**
 * *Osseux* (A94-14) : multiplicateur contre un ennemi brisé `broken` fois —
 * `1,5 + 0,5 × (broken − 1)`, 1 s'il est intact.
 */
export function brokenMultiplier(broken: number): number {
  if (!(broken > 0)) return 1;
  return BALANCE.boneBrokenMultiplier + BALANCE.boneBrokenStep * (Math.floor(broken) - 1);
}

/**
 * *Ferreux* : multiplicateur des dés Basiques — ×1,25 à 3 pièces, ×2^k à 5
 * pièces (`k` dés Maîtres à leur face maximale), les deux cumulés.
 */
export function ironBasicMultiplier(
  bonuses: readonly ActiveMaterialBonus[] | null | undefined,
  maxFaces: number,
): number {
  let mult = 1;
  if (hasMaterialBonus(bonuses, 'iron', 3)) mult *= BALANCE.ironBasicMultiplier;
  if (hasMaterialBonus(bonuses, 'iron', 5) && maxFaces > 0) {
    mult *= Math.pow(BALANCE.ironMaxFaceFactor, maxFaces);
  }
  return mult;
}
